import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, User } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { FileImage } from "@/components/FileImage";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader } from "@/components/Loader";

const STATUS_FILTERS = ["all", "application_pending", "approved_payment_pending", "active", "expired", "suspended"];

export default function AdminMembers() {
  const { t, lang } = useApp();
  const [params, setParams] = useSearchParams();
  const [members, setMembers] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const status = params.get("status") || "all";

  const load = useCallback(() => {
    const p = {};
    if (status !== "all") p.status = status;
    if (q) p.q = q;
    api.get("/admin/members", { params: p }).then(({ data }) => setMembers(data.members)).catch(() => setMembers([]));
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const setStatus = (s) => { const n = new URLSearchParams(params); if (s === "all") n.delete("status"); else n.set("status", s); setParams(n); };

  const act = async (id, action) => {
    try {
      await api.post(`/admin/members/${id}/${action}`);
      toast.success(t("saved"));
      setSelected(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB") : "—";

  return (
    <div className="fade-up">
      <h1 className="font-serif-cu text-3xl mb-6">{t("members")}</h1>

      <div className="flex items-center gap-2 cu-input px-4 py-2.5 mb-4">
        <Search size={16} className="text-[color:var(--cu-muted)]" />
        <input data-testid="members-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="bg-transparent outline-none text-sm w-full text-[color:var(--cu-text)]" />
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-none pb-1">
        {STATUS_FILTERS.map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`members-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-[0.68rem] uppercase tracking-[0.1em] font-mono-cu whitespace-nowrap transition-colors ${status === s ? "text-[color:var(--cu-black)]" : "text-[color:var(--cu-muted)] border border-[color:var(--cu-border)]"}`}
            style={status === s ? { background: "linear-gradient(135deg,#BF953F,#AA771C)" } : {}}>
            {s === "all" ? t("all") : s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {members === null ? <Loader /> : members.length === 0 ? (
        <p className="text-sm text-[color:var(--cu-muted)] py-10 text-center">No members found.</p>
      ) : (
        <div className="space-y-3">
          {members.map((m) => (
            <button key={m.id} onClick={() => setSelected(m)} data-testid={`member-row-${m.id}`} className="cu-card w-full p-4 flex items-center gap-4 text-left">
              <div className="w-11 h-11 rounded-full overflow-hidden gold-border shrink-0">
                <FileImage path={m.profile_photo_path} alt="" className="w-full h-full object-cover" fallback={<div className="w-full h-full flex items-center justify-center bg-[rgba(212,175,55,0.07)]"><User size={18} className="text-[color:var(--cu-gold)]" /></div>} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-serif-cu text-[color:var(--cu-text)] truncate">{m.first_name} {m.surname}</div>
                <div className="eyebrow mt-0.5 truncate">{m.membership_number || m.email}</div>
              </div>
              <StatusBadge status={m.status} />
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[color:var(--cu-charcoal)] border-[color:var(--cu-border)] text-[color:var(--cu-text)] max-h-[90vh] overflow-y-auto scrollbar-none">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif-cu text-xl flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden gold-border">
                    <FileImage path={selected.profile_photo_path} alt="" className="w-full h-full object-cover" fallback={<div className="w-full h-full flex items-center justify-center bg-[rgba(212,175,55,0.07)]"><User size={18} className="text-[color:var(--cu-gold)]" /></div>} />
                  </div>
                  {selected.first_name} {selected.surname}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-2 text-sm">
                <Det label={t("status")} value={<StatusBadge status={selected.status} />} />
                <Det label={t("email")} value={selected.email} />
                <Det label={t("phone")} value={selected.phone} />
                <Det label={t("country")} value={`${selected.city || ""}${selected.city ? ", " : ""}${selected.country || ""}`} />
                <Det label={t("date_of_birth")} value={selected.date_of_birth} />
                <Det label={t("preferred_city")} value={selected.preferred_city} />
                <Det label={t("how_discovered")} value={selected.how_discovered} />
                <Det label={t("member_no")} value={selected.membership_number || "—"} />
                <Det label={t("your_reference")} value={selected.payment_reference || "—"} />
                <Det label={t("joining_date")} value={fmt(selected.join_date)} />
                <Det label={t("renewal_date")} value={fmt(selected.renewal_date)} />
                {selected.payment_notified && <p className="text-xs text-amber-400 pt-1">● Member has notified payment</p>}
                {selected.introduction && <p className="text-xs text-[color:var(--cu-muted)] pt-2 italic">“{selected.introduction}”</p>}
              </div>

              <div className="grid grid-cols-2 gap-2 pt-4">
                {selected.status === "application_pending" && (
                  <>
                    <ActBtn testid="admin-approve-applicant-button" label={t("approve")} onClick={() => act(selected.id, "approve")} primary />
                    <ActBtn testid="admin-reject-button" label={t("reject")} onClick={() => act(selected.id, "reject")} />
                  </>
                )}
                {selected.status === "approved_payment_pending" && (
                  <ActBtn testid="admin-confirm-payment-button" label={t("confirm_payment")} onClick={() => act(selected.id, "confirm-payment")} primary wide />
                )}
                {selected.status === "active" && (
                  <>
                    <ActBtn testid="admin-renew-button" label={t("renew")} onClick={() => act(selected.id, "renew")} primary />
                    <ActBtn testid="admin-suspend-button" label={t("suspend")} onClick={() => act(selected.id, "suspend")} />
                  </>
                )}
                {(selected.status === "suspended" || selected.status === "expired") && (
                  <>
                    <ActBtn testid="admin-activate-button" label={t("activate")} onClick={() => act(selected.id, "activate")} primary />
                    <ActBtn testid="admin-renew-button2" label={t("renew")} onClick={() => act(selected.id, "renew")} />
                  </>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Det({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[color:var(--cu-border)] pb-1.5">
      <span className="eyebrow">{label}</span>
      <span className="text-right text-[color:var(--cu-text)]">{value}</span>
    </div>
  );
}
function ActBtn({ label, onClick, primary, wide, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`${primary ? "btn-gold" : "btn-ghost"} py-3 text-xs ${wide ? "col-span-2" : ""}`}>{label}</button>;
}
